import type { ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import { NativeButton } from '../common/NativeButton';
import { JourneyIcon, type JourneyIconName } from './journey-icons';
import { useJourneyPalette } from './journey-theme';

export type JourneyButtonKind = 'primary' | 'secondary' | 'text' | 'offer';

/**
 * The journey's action treatments (design reference `.p-button`):
 * primary = Tabbio violet fill, secondary = grey fill with a hairline,
 * text = plain secondary label, offer = the `.subscription-offer` pill of the
 * chooser (A) and paywall (B): text-coloured fill, background-coloured label,
 * fully rounded, 52pt. Built on `NativeButton` (press scale, haptics,
 * busy/disabled state). Height grows with Dynamic Type instead of clipping.
 */
export function JourneyButton({
  label,
  onPress,
  kind = 'primary',
  icon,
  disabled,
  loading,
  accessibilityLabel,
  accessibilityHint,
}: {
  label: string;
  onPress: () => void;
  kind?: JourneyButtonKind;
  icon?: JourneyIconName | ReactNode;
  disabled?: boolean;
  loading?: boolean;
  accessibilityLabel?: string;
  accessibilityHint?: string;
}) {
  const palette = useJourneyPalette();
  const textColor =
    kind === 'primary'
      ? '#ffffff'
      : kind === 'offer'
        ? palette.bg
        : kind === 'secondary'
          ? palette.text
          : palette.secondary;
  const iconNode =
    typeof icon === 'string' ? (
      <JourneyIcon name={icon as JourneyIconName} size={18} color={textColor} />
    ) : (
      icon
    );
  return (
    <NativeButton
      title={label}
      onPress={onPress}
      variant={kind === 'primary' || kind === 'offer' ? 'solid' : 'ghost'}
      color="primary"
      disabled={disabled}
      loading={loading}
      block
      haptic={kind === 'text' ? 'selection' : 'light'}
      iconLeft={iconNode ?? undefined}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      textColor={textColor}
      textStyle={[styles.label, kind === 'text' && styles.textLabel]}
      style={[
        styles.base,
        kind === 'primary' && { backgroundColor: palette.brandFill },
        kind === 'offer' && [styles.offer, { backgroundColor: palette.text }],
        kind === 'secondary' && {
          backgroundColor: palette.group,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: palette.line,
        },
        kind === 'text' && styles.text,
      ]}
    />
  );
}

const styles = StyleSheet.create({
  base: {
    height: undefined,
    minHeight: 50,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 14,
  },
  text: { minHeight: 44, paddingVertical: 8 },
  offer: { minHeight: 52, borderRadius: 999 },
  label: { fontSize: 16, fontWeight: '600', textAlign: 'center', flexShrink: 1 },
  textLabel: { fontSize: 14, fontWeight: '500' },
});
